import type { Viewport } from "@archlang/scene/board";

/**
 * Camera history — a browser-history ring buffer over the viewports the camera
 * comes to REST at. Every settle (auto-frame, drift-back, dial, spotlight
 * restore, or a manual gesture) pushes its final viewport; **⌃⌥← / ⌃⌥→** step
 * back / forward through them.
 *
 * Browser semantics:
 *  - `push` from mid-history truncates the forward stack (a new rest supersedes
 *    the branch you'd stepped back from);
 *  - stepping (`back`/`forward`) moves the cursor without pushing — the binder
 *    flags the resulting settle so it doesn't re-record a replayed position;
 *  - adjacent near-identical viewports dedupe (an idle re-render isn't a new
 *    place), and the buffer caps at `cap` (oldest dropped).
 *
 * Pure and DOM-free — the hook drives push/step and the animation.
 */
export interface CameraHistory {
  /** Record a rest viewport (dedupes vs the current entry; truncates forward). */
  push(vp: Viewport): void;
  /** Step to the previous entry, or null at the start. */
  back(): Viewport | null;
  /** Step to the next entry, or null at the end. */
  forward(): Viewport | null;
  /** Drop everything (workspace swap). */
  reset(): void;
}

const DEFAULT_CAP = 20;
// Two rests count as "the same place" within these tolerances (avoids logging an
// idle re-render or a sub-pixel settle as a distinct history entry).
const CENTER_EPS = 1; // world units
const ZOOM_EPS_LN = 0.02; // ≈ 2%

function samePath(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((s, i) => s === b[i]);
}

function near(a: Viewport, b: Viewport): boolean {
  return (
    samePath(a.focalPath, b.focalPath) &&
    Math.abs(a.center.x - b.center.x) <= CENTER_EPS &&
    Math.abs(a.center.y - b.center.y) <= CENTER_EPS &&
    Math.abs(Math.log(a.zoom / b.zoom)) <= ZOOM_EPS_LN
  );
}

export function createCameraHistory(cap = DEFAULT_CAP): CameraHistory {
  let entries: Viewport[] = [];
  let idx = -1; // cursor into `entries` (the position we're currently AT)

  return {
    push(vp) {
      const cur = entries[idx];
      if (cur && near(cur, vp)) return; // same place — nothing new
      // Truncate any forward branch, then append.
      entries = entries.slice(0, idx + 1);
      entries.push(vp);
      if (entries.length > cap) entries.shift();
      idx = entries.length - 1;
    },
    back() {
      if (idx <= 0) return null;
      idx -= 1;
      return entries[idx]!;
    },
    forward() {
      if (idx < 0 || idx >= entries.length - 1) return null;
      idx += 1;
      return entries[idx]!;
    },
    reset() {
      entries = [];
      idx = -1;
    },
  };
}
